import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { texts, useLang, type Lang } from './i18n.js';
import { navigate } from './router.js';
import { toast } from './ui.js';

/**
 * "Kom igång" for freelancers, editors and camera (owner 2026-10-18, thread "Onboarding för frilansare och klippare",
 * choice A + B): a short checklist the first time they open the Hub – the calendar, the project folder, the Premiere
 * panel and a project from start to finish – plus small tips where the same things come up in the work. The API only
 * remembers what each person ticked (/api/me/onboarding); Klara answers from the same facts (klaraHelp.ts WORKING), so
 * keep both true together.
 *
 * The Premiere panel step shows once the packaged panel is published at PANEL_FILE (apps/web/public/downloads/); until
 * then the step says the panel is on its way and is not counted.
 */

export const PANEL_FILE = '/downloads/kff-hub-premiere.ccx';

type Step = 'calendar' | 'folders' | 'panel' | 'workflow';
interface GuideState {
  for: boolean;
  done: Partial<Record<Step, string>>;
  dismissed: boolean;
}

const TX = texts(
  {
    title: 'Getting started',
    progress: (n: number, of: number) => `${n} of ${of} done`,
    intro: 'What you need to know before your first project. Open a step, read it, and tick it off.',
    show: 'Show',
    hide: 'Hide',
    done: 'Done',
    doneMark: 'Done',
    undo: 'Not done',
    close: 'Hide the guide',
    closed: 'The guide is hidden – open it again under "Getting started" in the account menu',
    closedLink: 'The guide is hidden – open it again with "Getting started" at the bottom of the page',
    allDone: 'All done – welcome to KFF!',
    reopen: 'Getting started',
    calendar: 'Add your dates to your calendar',
    calendarBody: 'Your shoots, draft dates and the time you plan show up in your own calendar (Google, Apple or Outlook) and stay up to date. You do this once.',
    calendarBtn: 'Add to your calendar',
    openCal: 'Open in my calendar app',
    copy: 'Copy address',
    copied: 'Address copied – paste it under "Add calendar from URL" in your calendar',
    google: 'In Google Calendar: Other calendars › + › From URL, paste the address.',
    folders: 'How the project folder works',
    foldersBody: 'Every project has the same folders in Google Drive. Work in them through Google Drive for desktop.',
    foldersAccessLink: 'When you are added to a project you get an email from Google with the project folder. Open it once and it shows under "Shared with me" in Drive for desktop. No email? Ask the producer.',
    foldersAccessStaff: 'You reach every project folder on the shared drive, and from the project page in the Hub (Files, or the project folder link).',
    brand: "The client's brand – fonts, logos, MOGRT and music – is in {Client} - Internal / Brand. The client's own folder (External) is what the client sees: never put anything there yourself.",
    naming: 'Name your exports like this',
    namingBody: 'Video_format_Ddraft_Vversion.mp4, for example Fiske_16x9_D1_V2.mp4. The Hub finds the right video from the name. Always MP4, never ProRes.',
    panel: 'Install the Premiere panel (optional)',
    panelBody: 'The panel shows the review comments inside Premiere Pro (25 or later): click a comment to jump there, add them all as markers, and tick them done.',
    panelSteps: [
      'Download the panel and double-click the file. Creative Cloud installs it.',
      'In Premiere: Window › UXP Plugins › KFF Hub.',
      'In the Hub, open "Connect Premiere" and copy the key. Paste it in the panel and click Connect.'
    ],
    panelBtn: 'Download the panel',
    panelSoon: 'The panel is on its way. We let you know when it is ready – you can skip this step until then.',
    rushesHead: 'Your footage goes in RUSHES',
    rushesBody: 'Upload to RUSHES/A-CAM, B-CAM, B-ROLL or DRONE through Google Drive for desktop the same day. The Hub reminds you 30 minutes after the shoot: press "Done" when everything is up, and the editor is told.',
    cameraSteps: [
      'You are added to a project: you get a notice in the bell, and the shoot shows under Your shoots on My work and in your calendar.',
      'Read the brief in the Hub and in 00_BRIEF before the shoot. Time, place and who is there are on the shoot.',
      'After the shoot: upload the footage to RUSHES and press "Done" on the yellow banner on My work.',
      'Sound goes in SOUND, stills in PHOTO. Questions go to the producer – the contact is on the project.'
    ],
    workflow: 'A project from start to finish',
    postMore: 'Folders, editing, export names, sound and colour, one step at a time:',
    postLink: 'Post production › Step by step',
    workflowSteps: [
      'You are added to a project: you get a notice in the bell, and the project shows under your projects and in your calendar.',
      'Read the brief in the Hub and in 00_BRIEF. Questions go to the producer – the contact is on the project.',
      'Plan when you will edit: "+ Plan" under a day in the week on My work. ▶ on the block starts the timer.',
      'The rushes are in RUSHES after the shoot. Save your Premiere project in ADOBE/PREMIERE.',
      'Export to EXPORTS/VIDEO with the right name and send it for review in the Hub.',
      'Comments come in the Hub and in the Premiere panel. Fix them, tick them done, export the next version.',
      'When the client has approved, the producer delivers. Then upload all project files and export a clean version without text and graphics.'
    ]
  },
  {
    title: 'Kom igång',
    progress: (n: number, of: number) => `${n} av ${of} klart`,
    intro: 'Det här behöver du kunna före ditt första projekt. Öppna ett steg, läs och bocka av.',
    show: 'Visa',
    hide: 'Dölj',
    done: 'Klart',
    doneMark: 'Klart',
    undo: 'Inte klart',
    close: 'Dölj guiden',
    closed: 'Guiden är dold – öppna den igen under "Kom igång" i kontomenyn',
    closedLink: 'Guiden är dold – öppna den igen med "Kom igång" längst ner på sidan',
    allDone: 'Allt klart – välkommen till KFF!',
    reopen: 'Kom igång',
    calendar: 'Lägg in dina datum i kalendern',
    calendarBody: 'Dina inspelningar, utkastdatum och den tid du planerar syns i din egen kalender (Google, Apple eller Outlook) och hålls uppdaterade. Du gör det en gång.',
    calendarBtn: 'Lägg till i din kalender',
    openCal: 'Öppna i min kalenderapp',
    copy: 'Kopiera adressen',
    copied: 'Adressen är kopierad – klistra in den under "Lägg till kalender via URL" i din kalender',
    google: 'I Google Kalender: Andra kalendrar › + › Via webbadress, klistra in adressen.',
    folders: 'Så fungerar projektmappen',
    foldersBody: 'Alla projekt har samma mappar i Google Drive. Arbeta i dem via Google Drive för datorn.',
    foldersAccessLink: 'När du läggs till i ett projekt får du ett mejl från Google med projektmappen. Öppna den en gång så finns den under "Delat med mig" i Drive för datorn. Inget mejl? Fråga producenten.',
    foldersAccessStaff: 'Du når alla projektmappar på den delade disken, och från projektsidan i Hubben (Filer, eller länken till projektmappen).',
    brand: 'Kundens varumärke – typsnitt, logor, MOGRT och musik – finns i {Kund} - Internal / Brand. Kundens egen mapp (External) är det kunden ser: lägg aldrig något där själv.',
    naming: 'Döp dina exporter så här',
    namingBody: 'Video_format_Dutkast_Vversion.mp4, till exempel Fiske_16x9_D1_V2.mp4. Hubben hittar rätt video på namnet. Alltid MP4, aldrig ProRes.',
    panel: 'Installera Premiere-panelen (valfritt)',
    panelBody: 'Panelen visar granskningskommentarerna inne i Premiere Pro (25 eller senare): klicka på en kommentar för att hoppa dit, lägg in alla som markörer och bocka av dem.',
    panelSteps: [
      'Ladda ner panelen och dubbelklicka på filen. Creative Cloud installerar den.',
      'I Premiere: Fönster › UXP-plugins › KFF Hub.',
      'Öppna "Koppla Premiere" i Hubben och kopiera nyckeln. Klistra in den i panelen och klicka Koppla.'
    ],
    panelBtn: 'Ladda ner panelen',
    panelSoon: 'Panelen är på väg. Vi säger till när den är klar – du kan hoppa över steget till dess.',
    rushesHead: 'Ditt material hamnar i RUSHES',
    rushesBody: 'Ladda upp till RUSHES/A-CAM, B-CAM, B-ROLL eller DRONE via Google Drive för datorn samma dag. Hubben påminner dig 30 minuter efter inspelningen: tryck "Klart" när allt är uppe, så får redigeraren veta.',
    cameraSteps: [
      'Du läggs till i ett projekt: du får en notis i klockan, och inspelningen syns under Dina inspelningar på Mitt arbete och i din kalender.',
      'Läs briefen i Hubben och i 00_BRIEF före inspelningen. Tid, plats och vilka som är med står på inspelningen.',
      'Efter inspelningen: ladda upp materialet i RUSHES och tryck "Klart" på den gula rutan på Mitt arbete.',
      'Ljud går i SOUND, stillbilder i PHOTO. Frågor går till producenten – kontakten står på projektet.'
    ],
    workflow: 'Ett projekt från start till slut',
    postMore: 'Mappar, klipp, exportnamn, ljud och färg, ett steg i taget:',
    postLink: 'Postproduktion › Steg för steg',
    workflowSteps: [
      'Du läggs till i ett projekt: du får en notis i klockan, och projektet syns bland dina projekt och i din kalender.',
      'Läs briefen i Hubben och i 00_BRIEF. Frågor går till producenten – kontakten står på projektet.',
      'Planera när du ska redigera: "+ Planera" under en dag i veckan på Mitt arbete. ▶ på blocket startar timern.',
      'Rushes finns i RUSHES efter inspelningen. Spara ditt Premiere-projekt i ADOBE/PREMIERE.',
      'Exportera till EXPORTS/VIDEO med rätt namn och skicka den för granskning i Hubben.',
      'Kommentarerna kommer i Hubben och i Premiere-panelen. Åtgärda, bocka av, exportera nästa version.',
      'När kunden godkänt levererar producenten. Ladda sedan upp alla projektfiler och exportera en ren version utan text och grafik.'
    ]
  }
);

/** The project folder template (spec FLOW-404), with what each folder is for. */
const FOLDERS: Record<Lang, [string, string][]> = {
  en: [
    ['00_BRIEF', 'brief and storyboard – read first'],
    ['AGREEMENTS', 'agreements – never touch'],
    ['RUSHES', 'A-CAM, B-CAM, B-ROLL, DRONE – the shoot'],
    ['SOUND', 'A-MIC, B-MIC, MUSIC, SFX'],
    ['ADOBE', 'PREMIERE, AE_PROJECT, PS_PROJECT – your project files'],
    ['GRAPHIC', 'graphics for the project'],
    ['PHOTO', 'stills'],
    ['EXPORTS', 'VIDEO and PHOTO – your exports for review and delivery']
  ],
  sv: [
    ['00_BRIEF', 'brief och storyboard – läs först'],
    ['AGREEMENTS', 'avtal – rör inte'],
    ['RUSHES', 'A-CAM, B-CAM, B-ROLL, DRONE – inspelningen'],
    ['SOUND', 'A-MIC, B-MIC, MUSIC, SFX'],
    ['ADOBE', 'PREMIERE, AE_PROJECT, PS_PROJECT – dina projektfiler'],
    ['GRAPHIC', 'grafik för projektet'],
    ['PHOTO', 'stillbilder'],
    ['EXPORTS', 'VIDEO och PHOTO – dina exporter för granskning och leverans']
  ]
};

export function FolderMap(p: { compact?: boolean }) {
  const lang = useLang();
  const rows = p.compact ? FOLDERS[lang].filter(([f]) => ['00_BRIEF', 'RUSHES', 'ADOBE', 'EXPORTS'].includes(f)) : FOLDERS[lang];
  return (
    <div class="guide-tree" role="group" aria-label={lang === 'sv' ? 'Projektmappen' : 'The project folder'}>
      <code class="guide-root">{lang === 'sv' ? '{Kund}_{Projekt}_{Månad}_{År}' : '{Client}_{Project}_{Month}_{Year}'}</code>
      <dl>
        {rows.map(([f, what]) => (
          <div key={f} class="guide-row">
            <dt>
              <code>{f}</code>
            </dt>
            <dd>{what}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Is the packaged panel published? A missing file falls back to the app's index.html, so look at the type too. */
export function usePanelReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    fetch(PANEL_FILE, { method: 'HEAD' }).then(
      (r) => setReady(r.ok && !(r.headers.get('content-type') ?? '').includes('text/html')),
      () => setReady(false)
    );
  }, []);
  return ready;
}

/** Whether the guide is still open for this person (for the card on My Work and the link page). */
export function useGuide(api: Api): [GuideState | null, (s: GuideState) => void] {
  const [s, setS] = useState<GuideState | null>(null);
  useEffect(() => {
    api.get<GuideState>('/api/me/onboarding').then(setS, () => setS(null));
  }, [api]);
  return [s, setS];
}

function CalendarStep(p: { api: Api; onMade: () => void }) {
  const tx = TX[useLang()];
  const [feed, setFeed] = useState<{ url: string; webcal: string } | null>(null);
  const make = async () => {
    try {
      setFeed(await p.api.post<{ url: string; webcal: string }>('/api/crew/calendar-feed', {}));
      p.onMade();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feed!.url);
      toast({ text: tx.copied });
    } catch {
      /* no clipboard: the address is shown to copy by hand */
    }
  };
  return (
    <>
      <p>{tx.calendarBody}</p>
      {feed ? (
        <>
          <p class="cluster">
            <a class="btn btn-primary" href={feed.webcal}>
              {tx.openCal}
            </a>
            <button type="button" class="btn" onClick={() => void copy()}>
              {tx.copy}
            </button>
          </p>
          <p class="small muted" style={{ overflowWrap: 'anywhere' }}>
            {feed.url}
          </p>
          <p class="small muted">{tx.google}</p>
        </>
      ) : (
        <p>
          <button type="button" class="btn btn-primary" onClick={() => void make()}>
            {tx.calendarBtn}
          </button>
        </p>
      )}
    </>
  );
}

function StepRow(p: { n: number; title: string; done: boolean; open: boolean; toggle: () => void; tick?: (done: boolean) => void; children: ComponentChildren }) {
  const tx = TX[useLang()];
  const id = `guide-step-${p.n}`;
  return (
    <li class={`guide-step${p.done ? ' is-done' : ''}`}>
      <button type="button" class="guide-head" aria-expanded={p.open} aria-controls={id} onClick={p.toggle}>
        <span class="guide-num" aria-hidden="true">
          {p.done ? '✓' : p.n}
        </span>
        <span class="guide-title">
          {p.title}
          {p.done ? <span class="sr-only"> – {tx.doneMark}</span> : null}
        </span>
        <span class="small muted">{p.open ? tx.hide : tx.show}</span>
      </button>
      {p.open ? (
        <div class="guide-body stack" id={id}>
          {p.children}
          {p.tick ? (
            <p>
              <button type="button" class={p.done ? 'btn btn-sm' : 'btn btn-primary btn-sm'} onClick={() => p.tick!(!p.done)}>
                {p.done ? tx.undo : tx.done}
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Which work the guide explains: editing, camera, or both (many freelancers do both, owner 2026-10-21). */
export type GuideWork = 'edit' | 'camera' | 'both';
export const guideWork = (roles: string[]): GuideWork => (roles.includes('camera') ? (roles.includes('editor') ? 'both' : 'camera') : 'edit');

/**
 * The checklist. `card`: on My Work and the link page, until the person ticks everything or hides it. `page`: opened
 * again from the account menu (staff) or the link page's footer, always shown, nothing to hide.
 */
export function GettingStarted(p: { api: Api; via: 'staff' | 'link'; mode: 'card' | 'page'; work?: GuideWork; /** Post production's step-by-step guide is on: point to it */ post?: boolean; state?: GuideState | null; onState?: (s: GuideState) => void }) {
  const lang = useLang();
  const tx = TX[lang];
  const [own, setOwn] = useState<GuideState | null>(p.state ?? null);
  const s = p.state !== undefined ? p.state : own;
  const set = (x: GuideState) => (p.onState ? p.onState(x) : setOwn(x));
  useEffect(() => {
    if (p.state === undefined) p.api.get<GuideState>('/api/me/onboarding').then(setOwn, () => setOwn(null));
  }, [p.api]);
  const panelReady = usePanelReady();
  const work = p.work ?? 'edit';
  // both: the editor's flow with the camera's rushes step after the brief
  const flow = work === 'camera' ? tx.cameraSteps : work === 'both' ? [...tx.workflowSteps.slice(0, 2), tx.cameraSteps[2]!, ...tx.workflowSteps.slice(2)] : tx.workflowSteps;
  const [open, setOpen] = useState<Step | null>(null);
  if (!s) return null;
  const core: Step[] = ['calendar', 'folders', 'workflow'];
  const n = core.filter((k) => s.done[k]).length;
  if (p.mode === 'card' && (!s.for || s.dismissed || n === core.length) && !(open && s.done[open])) return null;
  const tick = (step: Step | 'dismissed') => async (done: boolean) => {
    try {
      const next = await p.api.put<GuideState>(`/api/me/onboarding/${step}`, { done });
      set(next);
      if (step === 'dismissed') toast({ text: p.via === 'link' ? tx.closedLink : tx.closed });
      else if (done) setOpen(null);
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const reload = () => void p.api.get<GuideState>('/api/me/onboarding').then(set, () => undefined);
  const toggle = (k: Step) => () => setOpen(open === k ? null : k);
  return (
    <section class={`blk guide${p.mode === 'card' ? ' guide--card' : ''}`} aria-labelledby="guide-h" data-tour="guide">
      <div class="blk-head">
        <h2 class="blk-title" id="guide-h">
          {tx.title}
        </h2>
        <span class="small muted">{n === core.length ? tx.allDone : tx.progress(n, core.length)}</span>
      </div>
      <p class="blk-info">{tx.intro}</p>
      <ol class="guide-steps">
        <StepRow n={1} title={tx.calendar} done={!!s.done.calendar} open={open === 'calendar'} toggle={toggle('calendar')} tick={s.done.calendar ? tick('calendar') : undefined}>
          <CalendarStep api={p.api} onMade={reload} />
        </StepRow>
        <StepRow n={2} title={tx.folders} done={!!s.done.folders} open={open === 'folders'} toggle={toggle('folders')} tick={tick('folders')}>
          <p>{tx.foldersBody}</p>
          <FolderMap />
          <p class="small">{tx.brand}</p>
          <p class="small">{p.via === 'link' ? tx.foldersAccessLink : tx.foldersAccessStaff}</p>
          {/* camera: where the footage goes; editing: how exports are named (owner 2026-10-21: the guide follows the role) */}
          {work !== 'edit' ? (
            <>
              <p class="small-head">{tx.rushesHead}</p>
              <p class="small">{tx.rushesBody}</p>
            </>
          ) : null}
          {work !== 'camera' ? (
            <>
              <p class="small-head">{tx.naming}</p>
              <p class="small">{tx.namingBody}</p>
            </>
          ) : null}
        </StepRow>
        <StepRow n={3} title={tx.workflow} done={!!s.done.workflow} open={open === 'workflow'} toggle={toggle('workflow')} tick={tick('workflow')}>
          <ol class="guide-flow">
            {flow.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ol>
          {p.post ? (
            <p class="small">
              {tx.postMore}{' '}
              <a href="/post/guide" onClick={(e) => (e.preventDefault(), navigate('/post/guide'))}>
                {tx.postLink}
              </a>
            </p>
          ) : null}
        </StepRow>
        {work === 'camera' ? null : (
        <StepRow n={4} title={tx.panel} done={!!s.done.panel} open={open === 'panel'} toggle={toggle('panel')} tick={panelReady ? tick('panel') : undefined}>
          <p>{tx.panelBody}</p>
          {panelReady ? (
            <>
              <ol class="guide-flow">
                {tx.panelSteps.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ol>
              <p>
                <a class="btn" href={PANEL_FILE} download>
                  {tx.panelBtn}
                </a>
              </p>
            </>
          ) : (
            <p class="small muted">{tx.panelSoon}</p>
          )}
        </StepRow>
        )}
      </ol>
      {p.mode === 'card' ? (
        <p>
          <button type="button" class="btn btn-sm btn-ghost" onClick={() => void tick('dismissed')(true)}>
            {tx.close}
          </button>
        </p>
      ) : null}
    </section>
  );
}

/** The footer link on the personal link page that brings the guide back. */
export const reopenLabel = (lang: Lang) => TX[lang].reopen;

/**
 * A small tip where the work happens (B): shown until the person closes it on this device. Tips don't need to follow
 * people between devices, so they live in localStorage.
 */
export function Tip(p: { id: string; title: string; children: ComponentChildren }) {
  const lang = useLang();
  const key = `kff.tip.${p.id}`;
  const [shown, setShown] = useState(() => {
    try {
      return localStorage.getItem(key) !== '1';
    } catch {
      return true;
    }
  });
  if (!shown) return null;
  const close = () => {
    setShown(false);
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* private window: shown again next time */
    }
  };
  return (
    <div class="guide-tip">
      <div class="guide-tip-head">
        <strong>{p.title}</strong>
        <button type="button" class="btn btn-sm btn-ghost" onClick={close}>
          {lang === 'sv' ? 'Uppfattat' : 'Got it'}
        </button>
      </div>
      <div>{p.children}</div>
    </div>
  );
}

/** The tips' texts, shared by the screens that show them. */
export const TIPS = texts(
  {
    foldersTitle: 'Where things go',
    folders: 'Rushes are in RUSHES, your Premiere project goes in ADOBE/PREMIERE and your exports in EXPORTS/VIDEO.',
    namingTitle: 'Name the export',
    naming: 'Video_format_Ddraft_Vversion.mp4, for example Fiske_16x9_D1_V2.mp4 – the Hub finds the right video from the name. Always MP4.',
    panelTitle: 'See the comments in Premiere',
    panel: 'With the Premiere panel the comments show up as markers in your timeline. Install it under "Getting started".'
  },
  {
    foldersTitle: 'Var saker ligger',
    folders: 'Rushes finns i RUSHES, ditt Premiere-projekt sparas i ADOBE/PREMIERE och dina exporter i EXPORTS/VIDEO.',
    namingTitle: 'Döp exporten',
    naming: 'Video_format_Dutkast_Vversion.mp4, till exempel Fiske_16x9_D1_V2.mp4 – Hubben hittar rätt video på namnet. Alltid MP4.',
    panelTitle: 'Se kommentarerna i Premiere',
    panel: 'Med Premiere-panelen hamnar kommentarerna som markörer i din tidslinje. Installera den under "Kom igång".'
  }
);
